import { createFileRoute, Link } from "@tanstack/react-router";
import { claimAudio, registerAudioOwner, type AudioOwner } from "@/lib/audio-bus";
import { withMemberGate } from "@/lib/with-member-gate";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  Play,
  Pause,
  Square,
  Clock,
  ShieldCheck,
  HeartHandshake,
  Accessibility,
  Sparkles,
  Flower2,
  Maximize2,
  X,
  Sprout,
  Volume2,
  Search,
  SlidersHorizontal,
  ChevronDown,
} from "lucide-react";
import {
  YOGA_CATEGORIES,
  YOGA_GUIDE_HERO,
  type YogaPractice,
  type YogaCategory,
} from "@/lib/yoga-data";
import { getPoseImage } from "@/lib/yoga-pose-images";
import { YOGA_VISUAL_STEPS } from "@/lib/yoga-visual-steps";
import { StepCarousel } from "@/components/somatic/StepCarousel";
import {
  YOGA_NEEDS,
  YOGA_NEEDS_NOTE,
  YOGA_NEED_LABELS,
  YOGA_NEED_EMOJI,
  YOGA_LEVEL_LABELS,
  YOGA_POSITION_LABELS,
  YOGA_TIME_LABELS,
  YOGA_START_HERE_SLUG,
  getYogaMeta,
  timeBucket,
  type YogaNeed,
  type YogaLevel,
  type YogaPosition,
  type YogaTimeBucket,
} from "@/lib/yoga-meta";

import { useMembership } from "@/hooks/useMembership";
import {
  VOICE_IDS as EL_VOICE_IDS,
  prefetchTts,
  unlockAudioElement,
  cancelTts as cancelElTts,
  useTts,
} from "@/lib/elevenVoice";
import { MemberUpgradeCard } from "@/components/MemberUpgradeCard";
import { VolumeControl } from "@/components/VolumeControl";


export const Route = createFileRoute("/yoga")({
  head: () => ({
    meta: [
      { name: "robots", content: "noindex" },
      { title: "Trauma-Informed Yoga — Recovery Trauma™" },
      {
        name: "description",
        content:
          "A trauma-informed yoga studio with a consistent illustrated guide. Choice, autonomy, safety, and nervous system regulation through gentle movement.",
      },
      { property: "og:title", content: "Trauma-Informed Yoga — Recovery Trauma™" },
      { property: "og:description", content: "A trauma-informed yoga studio with a consistent illustrated guide. Choice, autonomy, safety, and nervous system regulation through gentle movement." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://recoverytrauma.com/yoga" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/yoga" }],
  }),
  component: withMemberGate(YogaPage, "Yoga"),
});

// First 2 practices in each of the first 2 categories are free preview
const FREE_PRACTICE_SLUGS = new Set<string>([
  "chair-yoga-for-safety",
  "first-steps-into-yoga",
  "grounding-yoga-flow",
  "grounding-strong-energy",
]);

function YogaPage() {
  const { isMember, loading } = useMembership();
  const [active, setActive] = useState<
    { practice: YogaPractice; category: YogaCategory } | null
  >(null);
  const [lightbox, setLightbox] = useState<{ src: string; alt: string } | null>(
    null,
  );
  const [query, setQuery] = useState("");
  const [need, setNeed] = useState<YogaNeed | null>(null);
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [level, setLevel] = useState<YogaLevel | null>(null);
  const [position, setPosition] = useState<YogaPosition | null>(null);
  const [time, setTime] = useState<YogaTimeBucket | null>(null);
  const [openFilter, setOpenFilter] = useState<"length" | "body" | "intensity" | null>(null);

  const q = query.trim().toLowerCase();
  const hasFilters = Boolean(q || need || categoryId || level || position || time);

  const resetFilters = () => {
    setQuery("");
    setNeed(null);
    setCategoryId(null);
    setLevel(null);
    setPosition(null);
    setTime(null);
  };

  const filteredCategories = useMemo<YogaCategory[]>(() => {
    return YOGA_CATEGORIES.map((cat) => {
      if (categoryId && cat.id !== categoryId) return { ...cat, practices: [] };
      const catMatches =
        !!q &&
        (cat.title.toLowerCase().includes(q) ||
          cat.description.toLowerCase().includes(q));
      const practices = cat.practices.filter((p) => {
        const meta = getYogaMeta(p.slug);
        if (need && !meta.needs.includes(need)) return false;
        if (level && meta.level !== level) return false;
        if (position && meta.position !== position) return false;
        if (time && timeBucket(p.duration) !== time) return false;
        if (!q) return true;
        return (
          catMatches ||
          p.title.toLowerCase().includes(q) ||
          p.blurb.toLowerCase().includes(q)
        );
      });
      return { ...cat, practices };
    }).filter((cat) => cat.practices.length > 0);
  }, [q, need, categoryId, level, position, time]);

  const totalMatches = useMemo(
    () => filteredCategories.reduce((n, c) => n + c.practices.length, 0),
    [filteredCategories],
  );

  const startHere = useMemo(() => {
    for (const cat of YOGA_CATEGORIES) {
      const practice = cat.practices.find((p) => p.slug === YOGA_START_HERE_SLUG);
      if (practice) return { practice, category: cat };
    }
    return null;
  }, []);

  const totalPractices = useMemo(
    () => YOGA_CATEGORIES.reduce((n, c) => n + c.practices.length, 0),
    [],
  );

  if (active) {
    const isFree = FREE_PRACTICE_SLUGS.has(active.practice.slug);
    const canView = isMember || isFree;
    return (
      <>
        <PracticeView
          practice={active.practice}
          category={active.category}
          onBack={() => setActive(null)}
          canView={canView}
          onZoom={(src, alt) => setLightbox({ src, alt })}
        />
        {lightbox && (
          <Lightbox
            src={lightbox.src}
            alt={lightbox.alt}
            onClose={() => setLightbox(null)}
          />
        )}
      </>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 sm:py-10">
      {/* ---------- Hero — compact, so the practices sit near the top ---------- */}
      <section className="relative mb-8 grid items-center gap-4 overflow-hidden rounded-3xl border border-gold/30 bg-gradient-to-br from-cream/70 via-card to-blush/20 p-4 sm:p-7 md:grid-cols-[1.15fr_1fr] sm:gap-6">
        <div>
          <span className="mb-2 inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border border-gold/40 bg-cream/60 px-3 py-1 text-xs font-medium text-foreground sm:mb-3">
            <Sparkles className="h-3 w-3 text-[var(--gold-deep)]" />
            Trauma-informed studio
          </span>
          <h1 className="font-serif text-2xl leading-tight text-foreground sm:text-4xl md:text-5xl">
            Trauma-Informed Yoga
          </h1>
          <p className="mt-2 max-w-xl text-sm text-muted-foreground sm:mt-3 sm:text-base">
            {totalPractices} illustrated practices across {YOGA_CATEGORIES.length}{" "}
            collections. Your body sets the pace — pause, modify or stop at any
            moment.
          </p>
          <div className="mt-3 hidden flex-wrap gap-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground min-[420px]:flex sm:mt-5">
            {["Choice", "Autonomy", "Safety", "Self-Compassion", "Regulation"].map(
              (w) => (
                <span
                  key={w}
                  className="rounded-full border border-border/60 bg-background/60 px-2.5 py-1"
                >
                  {w}
                </span>
              ),
            )}
          </div>
        </div>
        <div className="relative mx-auto w-full max-w-[120px] sm:max-w-xs md:max-w-sm">
          <div className="absolute inset-0 -z-10 rounded-full bg-[var(--gradient-gold)] opacity-30 blur-3xl" />
          <img
            loading="eager"
            decoding="async"
            src={YOGA_GUIDE_HERO}
            alt="Your trauma-informed yoga guide, seated calmly in meditation"
            width={1280}
            height={832}
            className="w-full rounded-2xl border border-gold/20 shadow-[var(--shadow-gold)]"
          />
        </div>
      </section>

      {/* ---------- Search + filters — right after the intro ---------- */}
      <section
        aria-label="Find a practice"
        className="sticky top-0 z-20 -mx-4 mb-8 border-b border-border/40 bg-background/85 px-4 py-3 backdrop-blur"
      >
        <label htmlFor="yoga-search" className="sr-only">
          Search yoga practices
        </label>
        <div className="relative">
          <Search className="pointer-events-none absolute start-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            id="yoga-search"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search practices — sleep, anxiety, chair, hips…"
            className="w-full rounded-full border border-border/60 bg-card px-11 py-3 text-sm text-foreground shadow-sm outline-none transition placeholder:text-muted-foreground focus:border-gold/50 focus:ring-2 focus:ring-gold/20 [&::-webkit-search-cancel-button]:hidden"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              aria-label="Clear search"
              className="absolute end-3 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Browse by topic — swipeable */}
        <p className="mt-3 mb-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
          Browse by topic
        </p>
        <div className="-mx-4 flex snap-x gap-2 overflow-x-auto px-4 pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <FilterChip
            label="All collections"
            selected={!categoryId}
            onClick={() => setCategoryId(null)}
          />
          {YOGA_CATEGORIES.map((cat) => (
            <FilterChip
              key={cat.id}
              label={`${cat.emoji} ${cat.title}`}
              selected={categoryId === cat.id}
              onClick={() =>
                setCategoryId(categoryId === cat.id ? null : cat.id)
              }
            />
          ))}
        </div>

        {/* Compact refinements row */}
        {(() => {
          const groups = [
            {
              key: "length" as const,
              label: "Length",
              value: time ? YOGA_TIME_LABELS[time] : null,
              options: (Object.keys(YOGA_TIME_LABELS) as YogaTimeBucket[]).map((t) => ({
                id: t, label: YOGA_TIME_LABELS[t], selected: time === t,
                pick: () => setTime(time === t ? null : t),
              })),
            },
            {
              key: "body" as const,
              label: "Body",
              value: position ? YOGA_POSITION_LABELS[position] : null,
              options: (Object.keys(YOGA_POSITION_LABELS) as YogaPosition[]).map((p) => ({
                id: p, label: YOGA_POSITION_LABELS[p], selected: position === p,
                pick: () => setPosition(position === p ? null : p),
              })),
            },
            {
              key: "intensity" as const,
              label: "Intensity",
              value: level ? YOGA_LEVEL_LABELS[level] : null,
              options: (Object.keys(YOGA_LEVEL_LABELS) as YogaLevel[]).map((l) => ({
                id: l, label: YOGA_LEVEL_LABELS[l], selected: level === l,
                pick: () => setLevel(level === l ? null : l),
              })),
            },
          ];
          const current = groups.find((g) => g.key === openFilter);
          return (
            <>
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                <span className="inline-flex items-center gap-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                  <SlidersHorizontal className="h-3.5 w-3.5" aria-hidden /> Filters
                </span>
                {groups.map((g) => (
                  <button
                    key={g.key}
                    type="button"
                    aria-expanded={openFilter === g.key}
                    onClick={() => setOpenFilter(openFilter === g.key ? null : g.key)}
                    className={`inline-flex min-h-9 max-w-[45vw] items-center gap-1 rounded-full border px-3 text-xs transition-colors ${
                      g.value || openFilter === g.key
                        ? "border-gold/60 bg-gold-light/50 font-medium text-foreground"
                        : "border-border/60 bg-card text-muted-foreground"
                    }`}
                  >
                    <span className="truncate">{g.value ?? g.label}</span>
                    <ChevronDown className={`h-3.5 w-3.5 shrink-0 transition-transform ${openFilter === g.key ? "rotate-180" : ""}`} aria-hidden />
                  </button>
                ))}
                {hasFilters && (
                  <button
                    type="button"
                    onClick={() => { resetFilters(); setOpenFilter(null); }}
                    className="inline-flex min-h-9 items-center gap-1 rounded-full px-2 text-xs text-[var(--gold-deep)] underline-offset-2 hover:underline"
                  >
                    <X className="h-3 w-3" /> Clear filters
                  </button>
                )}
              </div>
              {current && (
                <div role="group" aria-label={`${current.label} options`} className="mt-2 flex flex-wrap gap-1.5 rounded-2xl border border-gold/25 bg-card p-2">
                  {current.options.map((o) => (
                    <FilterChip
                      key={o.id}
                      label={`${o.selected ? "✓ " : ""}${o.label}`}
                      selected={o.selected}
                      onClick={() => { o.pick(); setOpenFilter(null); }}
                    />
                  ))}
                </div>
              )}
            </>
          );
        })()}

        {hasFilters && (
          <p className="mt-2 text-xs text-muted-foreground">
            {totalMatches === 0
              ? "No practices match — try fewer filters."
              : `${totalMatches} practice${totalMatches === 1 ? "" : "s"} match.`}
          </p>
        )}
      </section>

      {/* ---------- How are you arriving? ---------- */}
      <section aria-labelledby="yoga-need-heading" className="mb-8">
        <h2
          id="yoga-need-heading"
          className="mb-1 font-serif text-xl text-foreground"
        >
          How are you arriving today?
        </h2>
        <p className="mb-3 text-sm text-muted-foreground">
          Choose what's true right now — nothing here is a prescription.
        </p>
        <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-4">
          {YOGA_NEEDS.map((n) => {
            const selected = need === n.id;
            return (
              <button
                key={n.id}
                type="button"
                aria-pressed={selected}
                onClick={() => setNeed(selected ? null : n.id)}
                className={`flex flex-col items-start gap-1.5 rounded-2xl border p-4 text-start transition-all hover:-translate-y-0.5 ${
                  selected
                    ? "border-gold/60 bg-gold-light/40 shadow-[var(--shadow-gold)]"
                    : "border-border/50 bg-card hover:border-gold/40"
                }`}
              >
                <span className="text-2xl leading-none">{n.emoji}</span>
                <span className="font-serif text-lg leading-snug text-foreground">
                  {n.label}
                </span>
                <span className="text-xs leading-snug text-muted-foreground">
                  {n.blurb}
                </span>
                <span className="mt-1 text-[11px] leading-snug text-[var(--gold-deep)]">
                  {n.guidance}
                </span>
              </button>
            );
          })}
        </div>
        <p className="mt-3 rounded-2xl border border-border/50 bg-cream/40 p-3.5 text-xs leading-relaxed text-muted-foreground">
          {YOGA_NEEDS_NOTE}
        </p>
      </section>

      {/* ---------- Start here (only when nothing is filtered) ---------- */}
      {!hasFilters && startHere && (
        <section className="mb-8 overflow-hidden rounded-3xl border border-gold/30 bg-gradient-to-br from-cream/50 via-card to-card sm:grid sm:grid-cols-[200px_1fr]">
          <img
            src={getPoseImage(startHere.practice.slug, startHere.category.image)}
            alt={`${startHere.practice.title} — illustrated demonstration`}
            loading="lazy"
            width={1024}
            height={1024}
            className="hidden h-full w-full object-cover sm:block"
          />
          <div className="p-5 sm:p-6">
            <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--gold-deep)]">
              New here? Start with this
            </span>
            <p className="mt-1.5 font-serif text-xl text-foreground">
              {startHere.practice.title}
            </p>
            <p className="mt-1.5 text-sm text-muted-foreground">
              {startHere.practice.blurb}
            </p>
            <button
              type="button"
              onClick={() => setActive(startHere)}
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-[var(--gradient-gold)] px-5 py-2.5 text-sm font-medium text-foreground shadow-[var(--shadow-gold)] transition-transform hover:-translate-y-0.5"
            >
              <Play className="h-3.5 w-3.5" /> Begin · {startHere.practice.duration}
            </button>
          </div>
        </section>
      )}

      {/* ---------- Gentle note ---------- */}
      <div className="mx-auto mb-8 max-w-3xl rounded-2xl border border-gold/30 bg-gradient-to-br from-cream/60 via-card to-blush/20 p-5 text-center">
        <Flower2 className="mx-auto mb-2.5 h-5 w-5 text-[var(--gold-deep)]" />
        <p className="font-serif text-base leading-relaxed text-foreground sm:text-lg">
          “You are always in control. You can pause, skip, modify, or stop any
          practice at any time. There is no right way to do this. Your body
          leads the pace.”
        </p>
      </div>

      {/* Member status / upgrade */}
      {!loading && !isMember && (
        <div className="mb-8">
          <MemberUpgradeCard
            title="Unlock the full yoga studio"
            body={`You're exploring on a free account (membership payment required). A few practices are open below — full access unlocks every illustrated sequence.`}
            ctaLabel="Become a member"
          />
        </div>
      )}


      {/* ---------- Collections ---------- */}
      <div className="space-y-8 sm:space-y-14">
        {filteredCategories.map((cat) => (
          <section key={cat.id} id={`collection-${cat.id}`}>
            {/* Category header with image */}
            <div className="mb-4 grid grid-cols-[88px_1fr] items-center gap-4 overflow-hidden rounded-3xl border border-border/50 bg-gradient-to-br from-cream/40 via-card to-card sm:mb-6 sm:gap-6 md:grid-cols-[240px_1fr]">
              <button
                onClick={() =>
                  setLightbox({
                    src: cat.image,
                    alt: `${cat.title} — illustrated guide`,
                  })
                }
                className="group relative aspect-square w-full overflow-hidden bg-cream/60"
                aria-label={`Expand ${cat.title} image`}
              >
                <img
                  src={cat.image}
                  alt={`${cat.title} — guide demonstration`}
                  loading="lazy"
                  width={1024}
                  height={1024}
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <span className="absolute end-3 top-3 rounded-full bg-background/80 p-1.5 opacity-0 backdrop-blur transition-opacity group-hover:opacity-100">
                  <Maximize2 className="h-3.5 w-3.5 text-foreground" />
                </span>
              </button>
              <div className="p-4 md:p-5 md:pe-8">
                <div className="flex items-start gap-3">
                  <span className="text-2xl leading-none sm:text-3xl">{cat.emoji}</span>
                  <div className="min-w-0">
                    <h2 className="font-serif text-lg text-foreground sm:text-2xl">
                      {cat.title}
                    </h2>
                    <p className="mt-1 text-xs text-muted-foreground sm:text-sm">
                      {cat.description}
                    </p>
                  </div>
                </div>
                <ul className="mt-3 hidden flex-wrap gap-x-4 gap-y-1.5 text-xs text-muted-foreground sm:mt-4 sm:flex">
                  {cat.benefits.map((b) => (
                    <li key={b} className="inline-flex items-center gap-1.5">
                      <Sprout className="h-3 w-3 text-[var(--gold-deep)]" />
                      {b}
                    </li>
                  ))}
                </ul>
                <p className="mt-3 text-[11px] uppercase tracking-wider text-muted-foreground">
                  {cat.practices.length} practice
                  {cat.practices.length === 1 ? "" : "s"}
                </p>
              </div>
            </div>

            <div className="-mx-4 flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 pb-2 sm:mx-0 sm:grid sm:snap-none sm:grid-cols-2 sm:gap-4 sm:overflow-visible sm:px-0 sm:pb-0 lg:grid-cols-3">
              {cat.practices.map((p) => (
                <div
                  key={p.slug}
                  className="w-[78vw] max-w-[320px] shrink-0 snap-start sm:w-auto sm:max-w-none sm:shrink"
                >
                  <PracticeCard
                    practice={p}
                    category={cat}
                    isMember={isMember}
                    onOpen={() => setActive({ practice: p, category: cat })}
                  />
                </div>
              ))}
            </div>
          </section>
        ))}

        {filteredCategories.length === 0 && (
          <div className="rounded-3xl border border-border/50 bg-card p-10 text-center">
            <Flower2 className="mx-auto mb-3 h-6 w-6 text-[var(--gold-deep)]" />
            <p className="font-serif text-lg text-foreground">
              Nothing matches those choices
            </p>
            <p className="mt-1.5 text-sm text-muted-foreground">
              Try clearing a filter — the whole studio is still here.
            </p>
            <button
              type="button"
              onClick={resetFilters}
              className="mt-4 inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border border-gold/40 px-4 py-2 text-sm text-foreground hover:bg-gold-light/30"
            >
              Show all practices
            </button>
          </div>
        )}
      </div>

      <div className="mt-12 rounded-2xl border border-border/40 bg-cream/40 p-5 text-center text-sm text-muted-foreground">
        These practices support — they don't replace — therapy or medical care.
        Stop anytime your body signals it's too much.{" "}
        <Link to="/resources" className="text-primary hover:underline">
          Crisis resources
        </Link>
        .
      </div>

      {lightbox && (
        <Lightbox
          src={lightbox.src}
          alt={lightbox.alt}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
}

function FilterChip({
  label,
  selected,
  onClick,
  small,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
  small?: boolean;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={`min-h-9 shrink-0 whitespace-nowrap rounded-full border transition-colors ${
        small ? "px-2.5 py-1 text-[11px]" : "px-3.5 py-1.5 text-xs"
      } ${
        selected
          ? "border-gold/60 bg-gold-light/50 font-medium text-foreground"
          : "border-border/60 bg-card text-muted-foreground hover:border-gold/40 hover:text-foreground"
      }`}
    >
      {label}
    </button>
  );
}

function PracticeCard({
  practice,
  category,
  isMember,
  onOpen,
}: {
  practice: YogaPractice;
  category: YogaCategory;
  isMember: boolean;
  onOpen: () => void;
}) {
  const meta = getYogaMeta(practice.slug);
  const isFree = FREE_PRACTICE_SLUGS.has(practice.slug);
  const locked = !isMember && !isFree;
  return (
    <button
      onClick={onOpen}
      className="group relative flex flex-col overflow-hidden rounded-2xl border border-border/50 bg-card text-start transition-all hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-[var(--shadow-gold)]"
    >
      <div className="relative aspect-[5/3] overflow-hidden bg-cream/60">
        <img
          src={getPoseImage(practice.slug, category.image)}
          alt={`${practice.title} — illustrated demonstration`}
          loading="lazy"
          width={1024}
          height={1024}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        {locked && (
          <span className="absolute end-2 top-2 inline-flex items-center gap-1 rounded-full bg-gold-light/80 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[var(--gold-deep)] backdrop-blur">
            Member
          </span>
        )}
        {isFree && !isMember && (
          <span className="absolute end-2 top-2 rounded-full bg-cream/90 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground backdrop-blur">
            Preview
          </span>
        )}
      </div>
      <div className="flex flex-1 flex-col p-5">
        <p className="font-serif text-lg text-foreground">{practice.title}</p>
        <p className="mt-1.5 flex-1 text-sm text-muted-foreground">
          {practice.blurb}
        </p>
        {meta.needs.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {meta.needs.map((n) => (
              <span
                key={n}
                className="rounded-full border border-gold/40 bg-gold-light/30 px-2 py-0.5 text-[10px] font-medium text-[var(--gold-deep)]"
              >
                {YOGA_NEED_EMOJI[n]} {YOGA_NEED_LABELS[n]}
              </span>
            ))}
          </div>
        )}
        <div className="mt-3 flex flex-wrap gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
          <span className="rounded-full border border-border/60 px-2 py-0.5">
            {YOGA_LEVEL_LABELS[meta.level]}
          </span>
          <span className="rounded-full border border-border/60 px-2 py-0.5">
            {YOGA_POSITION_LABELS[meta.position]}
          </span>
        </div>
        <div className="mt-4 flex items-center justify-between text-xs">
          <span className="inline-flex items-center gap-1 text-muted-foreground">
            <Clock className="h-3 w-3" /> {practice.duration}
          </span>
          <span className="inline-flex items-center gap-1 text-[var(--gold-deep)] transition-transform group-hover:translate-x-0.5">
            Open <Play className="h-3 w-3" />
          </span>
        </div>
      </div>
    </button>
  );
}


function PracticeView({
  practice,
  category,
  onBack,
  canView,
  onZoom,
}: {
  practice: YogaPractice;
  category: YogaCategory;
  onBack: () => void;
  canView: boolean;
  onZoom: (src: string, alt: string) => void;
}) {
  return (
    <div className="mx-auto max-w-4xl px-4 py-10">
      <button
        onClick={onBack}
        className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Back to yoga studio
      </button>

      {/* Hero pose image / step-by-step illustrations */}
      <div className="grid items-center gap-8 md:grid-cols-[1fr_1fr]">
        {YOGA_VISUAL_STEPS[practice.slug] ? (
          <StepCarousel
            slides={YOGA_VISUAL_STEPS[practice.slug]}
            title={practice.title}
          />
        ) : (
        <button
          onClick={() =>
            onZoom(
              getPoseImage(practice.slug, category.image),
              `${practice.title} — pose illustration`,
            )
          }
          className="group relative overflow-hidden rounded-3xl border border-gold/30 bg-cream/60 shadow-[var(--shadow-gold)]"
          aria-label="Expand image to full screen"
        >
          <img loading="lazy" decoding="async"
            src={getPoseImage(practice.slug, category.image)}
            alt={`${practice.title} — pose illustration`}
            width={1024}
            height={1024}
            className="aspect-square w-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <span className="absolute end-3 top-3 inline-flex items-center gap-1 rounded-full bg-background/80 px-2.5 py-1 text-[11px] font-medium text-foreground backdrop-blur">
            <Maximize2 className="h-3 w-3" /> Expand
          </span>
        </button>
        )}

        <div>
          <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--gold-deep)]">
            {category.emoji} {category.title}
          </span>
          <h1 className="mt-2 font-serif text-3xl text-foreground md:text-4xl">
            {practice.title}
          </h1>
          <p className="mt-2 text-muted-foreground">{practice.blurb}</p>
          <p className="mt-2 inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-[var(--gold-deep)]">
            <Clock className="h-3 w-3" /> {practice.duration}
          </p>

          {/* Benefits */}
          <div className="mt-5 rounded-2xl border border-gold/20 bg-gradient-to-br from-cream/50 to-card p-4">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-[var(--gold-deep)]">
              Benefits
            </p>
            <ul className="space-y-1.5 text-sm text-foreground">
              {category.benefits.map((b) => (
                <li key={b} className="flex gap-2">
                  <Sprout className="mt-0.5 h-3.5 w-3.5 flex-none text-[var(--gold-deep)]" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {!canView ? (
        <div className="mt-8">
          <MemberUpgradeCard
            title="This practice is for members"
            body="Unlock the full Trauma-Informed Yoga studio — every illustrated guide, safety note, modification, and accessibility option."
          />
        </div>
      ) : (
        <>
          {/* Trauma-informed reminder */}
          <div className="mt-8 rounded-2xl border border-blush/40 bg-blush/15 p-5">
            <p className="text-sm font-medium text-foreground">
              🤍 Before you begin
            </p>
            <ul className="mt-2 space-y-1.5 text-sm text-muted-foreground">
              <li>You may stop at any time.</li>
              <li>Adjust the pose to what feels safe in your body.</li>
              <li>There is no right way to do this.</li>
              <li>Keep your eyes open if that feels more comfortable.</li>
            </ul>
          </div>

          {/* Audio guidance */}
          <AudioGuide
            title={practice.title}
            steps={practice.script}
          />

          {/* Guided script */}
          <section className="mt-6 rounded-2xl border border-gold/30 bg-card p-6 md:p-8">
            <h2 className="font-serif text-xl text-foreground">
              How to practice
            </h2>
            <ol className="mt-4 space-y-3">
              {practice.script.map((step, i) => (
                <li key={i} className="flex gap-3">
                  <span className="mt-0.5 inline-flex h-6 w-6 flex-none items-center justify-center rounded-full bg-[var(--gradient-gold)] text-xs font-semibold text-foreground">
                    {i + 1}
                  </span>
                  <p className="text-[15px] leading-relaxed text-foreground">
                    {step}
                  </p>
                </li>
              ))}
            </ol>
          </section>

          {/* Safety / Modifications / Accessibility */}
          <div className="mt-6 grid gap-4 md:grid-cols-3">
            <InfoCard
              icon={ShieldCheck}
              title="Trauma-informed safety"
              items={practice.safety}
              tone="rose"
            />
            <InfoCard
              icon={HeartHandshake}
              title="Modifications"
              items={practice.modifications}
              tone="gold"
            />
            <InfoCard
              icon={Accessibility}
              title="Accessibility"
              items={practice.accessibility}
              tone="sage"
            />
          </div>

          <div className="mt-8 rounded-2xl border border-border/40 bg-cream/40 p-5 text-center text-sm text-muted-foreground">
            Your body is the teacher. Pause, modify, or stop at any time.{" "}
            <Link to="/resources" className="text-primary hover:underline">
              Crisis resources
            </Link>
            .
          </div>
        </>
      )}
    </div>
  );
}

function InfoCard({
  icon: Icon,
  title,
  items,
  tone,
}: {
  icon: React.ElementType;
  title: string;
  items: string[];
  tone: "rose" | "gold" | "sage";
}) {
  const toneClass =
    tone === "rose"
      ? "from-blush/30 to-card border-blush/40"
      : tone === "gold"
        ? "from-gold-light/30 to-card border-gold/30"
        : "from-cream/60 to-card border-border/50";
  return (
    <div className={`rounded-2xl border bg-gradient-to-br p-5 ${toneClass}`}>
      <div className="mb-2 flex items-center gap-2">
        <Icon className="h-4 w-4 text-[var(--gold-deep)]" />
        <h3 className="font-serif text-base text-foreground">{title}</h3>
      </div>
      <ul className="space-y-2 text-sm text-muted-foreground">
        {items.map((item, i) => (
          <li key={i} className="flex gap-2">
            <span className="mt-1.5 inline-block h-1 w-1 flex-none rounded-full bg-[var(--gold-deep)]" />
            <span className="leading-relaxed">{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Lightbox({
  src,
  alt,
  onClose,
}: {
  src: string;
  alt: string;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4 backdrop-blur animate-fade-in"
      role="dialog"
      aria-modal="true"
    >
      <button
        onClick={onClose}
        className="absolute end-4 top-4 inline-flex h-11 w-11 touch-manipulation items-center justify-center rounded-full bg-background/90 text-foreground shadow hover:bg-background sm:h-10 sm:w-10"
        aria-label="Close"
      >
        <X className="h-5 w-5" />
      </button>
      <img loading="lazy" decoding="async"
        src={src}
        alt={alt}
        className="max-h-[90vh] max-w-[95vw] rounded-2xl object-contain shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
}

const TRAUMA_AFFIRMATIONS = [
  "You are safe in this moment.",
  "There is no right way to do this.",
  "Your body knows the pace.",
  "You can pause whenever you need.",
  "Notice what feels supportive.",
  "You are allowed to soften.",
];

type GuideStatus = "idle" | "playing" | "paused";

function AudioGuide({ title, steps }: { title: string; steps: string[] }) {
  // Registered with the global audio bus so any other track stops this guide.
  const SPEEDS = [0.75, 0.85, 1.0, 1.25] as const;
  type Speed = (typeof SPEEDS)[number];
  const { caller: ttsCaller } = useTts();

  const [status, setStatus] = useState<GuideStatus>("idle");
  const [currentStep, setCurrentStep] = useState(0);
  const [supported, setSupported] = useState(true);
  const [speed, setSpeed] = useState<Speed>(() => {
    const saved =
      typeof window !== "undefined" ? localStorage.getItem("yoga-audio-speed") : null;
    const n = saved ? parseFloat(saved) : null;
    return SPEEDS.includes(n as Speed) ? (n as Speed) : 0.85;
  });
  const [volume, setVolume] = useState<number>(() => {
    const saved = typeof window !== "undefined" ? localStorage.getItem("yoga-audio-volume") : null;
    const n = saved ? parseFloat(saved) : NaN;
    return Number.isFinite(n) ? Math.max(0, Math.min(1, n)) : 0.9;
  });
  const [muted, setMuted] = useState<boolean>(() =>
    typeof window !== "undefined" ? localStorage.getItem("yoga-audio-muted") === "1" : false,
  );
  const queueRef = useRef<string[]>([]);
  const indexRef = useRef(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const statusRef = useRef<typeof status>(status);

  useEffect(() => { statusRef.current = status; }, [status]);

  useEffect(() => {
    const v = muted ? 0 : volume;
    if (audioRef.current) audioRef.current.volume = v;
    try {
      localStorage.setItem("yoga-audio-volume", String(volume));
      localStorage.setItem("yoga-audio-muted", muted ? "1" : "0");
    } catch {}
  }, [volume, muted]);

  const stopAudio = () => {
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch {}
      audioRef.current.onended = null;
      audioRef.current = null;
    }
    cancelElTts();
  };

  useEffect(() => {
    setSupported(typeof window !== "undefined");
    return () => stopAudio();
  }, []);

  // Stop audio if the practice (steps) changes
  useEffect(() => {
    stopAudio();
    setStatus("idle");
    setCurrentStep(0);
    indexRef.current = 0;
  }, [steps]);

  const persistSpeed = (s: Speed) => {
    setSpeed(s);
    if (audioRef.current) audioRef.current.playbackRate = s;
    localStorage.setItem("yoga-audio-speed", String(s));
  };

  const buildQueue = () => {
    const opening = [
      `Welcome. We'll move gently through ${title}.`,
      "There's no pressure here. You're in control of this practice — you can pause, change, or stop at any moment.",
      "If anything feels like too much, please listen to your body first.",
    ];
    const body: string[] = [];
    steps.forEach((step, i) => {
      body.push(`When you're ready…`);
      body.push(step);
      if ((i + 1) % 2 === 0 && i < steps.length - 1) {
        const aff = TRAUMA_AFFIRMATIONS[i % TRAUMA_AFFIRMATIONS.length];
        body.push(aff);
      }
    });
    const closing = [
      "We're gently bringing the practice to a close.",
      "Notice how your body feels now, with kindness and without judgment.",
      "Thank you for showing up for yourself today.",
    ];
    return [...opening, ...body, ...closing];
  };

  const speakAt = async (i: number) => {
    const queue = queueRef.current;
    if (i >= queue.length) {
      setStatus("idle");
      setCurrentStep(0);
      indexRef.current = 0;
      return;
    }
    indexRef.current = i;
    const text = queue[i];

    let stepCount = -1;
    for (let k = 0; k <= i; k++) {
      if (/^When you're ready…$/.test(queue[k])) stepCount++;
    }
    if (stepCount >= 0) setCurrentStep(Math.min(stepCount, steps.length - 1));

    try {
      const activeAudio = audioRef.current ?? new Audio();
      audioRef.current = activeAudio;
      const url = await prefetchTts(ttsCaller, text, EL_VOICE_IDS.female);
      if (statusRef.current !== "playing") return;
      const audio = audioRef.current === activeAudio ? activeAudio : new Audio();
      audio.volume = muted ? 0 : volume;
      audio.playbackRate = speed;
      audio.onended = () => {
        if (statusRef.current !== "playing") return;
        window.setTimeout(() => {
          if (statusRef.current === "playing") void speakAt(i + 1);
        }, 600);
      };
      audioRef.current = audio;
      audio.src = url;
      await audio.play();
    } catch (err) {
      console.error("Yoga TTS error", err);
      setStatus("idle");
    }
  };

  const stopForBus = () => {
    if (audioRef.current) { try { audioRef.current.pause(); } catch {} }
    cancelElTts();
    setStatus("paused");
    statusRef.current = "paused";
  };
  const stopForBusRef = useRef(stopForBus);
  stopForBusRef.current = stopForBus;
  const ownerRef = useRef<AudioOwner>({
    stop: () => stopForBusRef.current(),
    ownsElement: (el) => audioRef.current === el,
  });
  useEffect(() => registerAudioOwner(ownerRef.current), []);

  const handlePlay = () => {
    if (!supported) return;
    claimAudio(ownerRef.current);
    if (status === "paused" && audioRef.current) {
      setStatus("playing");
      statusRef.current = "playing";
      void audioRef.current.play().catch(() => {});
      return;
    }
    stopAudio();
    audioRef.current = new Audio();
    unlockAudioElement(audioRef.current, muted ? 0 : volume);
    queueRef.current = buildQueue();
    setStatus("playing");
    statusRef.current = "playing";
    setCurrentStep(0);
    void speakAt(0);
  };

  const handlePause = () => {
    if (!supported) return;
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch {}
    }
    setStatus("paused");
  };

  const handleStop = () => {
    if (!supported) return;
    stopAudio();
    setStatus("idle");
    setCurrentStep(0);
    indexRef.current = 0;
  };

  return (
    <section className="mt-6 rounded-2xl border border-gold/30 bg-gradient-to-br from-cream/60 via-card to-blush/20 p-5">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 inline-flex h-9 w-9 flex-none items-center justify-center rounded-full bg-[var(--gradient-gold)] text-foreground">
            <Volume2 className="h-4 w-4" />
          </span>
          <div>
            <p className="font-serif text-base text-foreground">
              Voice-guided practice
            </p>
            <p className="text-xs text-muted-foreground">
              A gentle voice will walk you through each step at a calm pace,
              with trauma-informed reminders.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Speed selector */}
          <div className="me-1 hidden items-center gap-1 sm:flex">
            <span className="text-[11px] font-medium text-muted-foreground">
              Speed
            </span>
            {SPEEDS.map((s) => (
              <button
                key={s}
                onClick={() => persistSpeed(s)}
                disabled={status === "playing"}
                className={`rounded-md px-2 py-1 text-[11px] font-medium transition-colors ${
                  speed === s
                    ? "bg-[var(--gradient-gold)] text-foreground shadow-[var(--shadow-gold)]"
                    : "border border-border/60 bg-background/60 text-muted-foreground hover:text-foreground"
                } disabled:opacity-50`}
                aria-label={`Set speed to ${s}x`}
                title={`${s}x${s === 0.85 ? " (default, trauma-informed pace)" : ""}`}
              >
                {s}x
              </button>
            ))}
          </div>
          {status !== "playing" ? (
            <button
              onClick={handlePlay}
              disabled={!supported}
              className="inline-flex items-center gap-1.5 rounded-full bg-[var(--gradient-gold)] px-4 py-2 text-sm font-medium text-foreground shadow-[var(--shadow-gold)] transition-transform hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Play className="h-3.5 w-3.5" />
              {status === "paused" ? "Resume" : "Play"}
            </button>
          ) : (
            <button
              onClick={handlePause}
              className="inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border border-gold/40 bg-background/70 px-4 py-2 text-sm font-medium text-foreground hover:bg-background"
            >
              <Pause className="h-3.5 w-3.5" />
              Pause
            </button>
          )}
          <VolumeControl
            value={volume}
            onChange={(v) => {
              setVolume(v);
              if (v > 0 && muted) setMuted(false);
            }}
            muted={muted}
            onToggleMute={() => setMuted((m) => !m)}
            size="sm"
            ariaLabel="Yoga voice volume"
          />
          
          <button
            onClick={handleStop}
            disabled={!supported || status === "idle"}
            className="inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border border-border/60 bg-background/60 px-3 py-2 text-sm text-muted-foreground hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Stop audio guidance"
          >
            <Square className="h-3.5 w-3.5" />
            Stop
          </button>
        </div>
      </div>
      {/* Mobile speed row */}
      <div className="mt-3 flex items-center gap-1 sm:hidden">
        <span className="text-[11px] font-medium text-muted-foreground">
          Speed
        </span>
        {SPEEDS.map((s) => (
          <button
            key={s}
            onClick={() => persistSpeed(s)}
            disabled={status === "playing"}
            className={`rounded-md px-2 py-1 text-[11px] font-medium transition-colors ${
              speed === s
                ? "bg-[var(--gradient-gold)] text-foreground shadow-[var(--shadow-gold)]"
                : "border border-border/60 bg-background/60 text-muted-foreground hover:text-foreground"
            } disabled:opacity-50`}
            aria-label={`Set speed to ${s}x`}
          >
            {s}x
          </button>
        ))}
      </div>
      {!supported && (
        <p className="mt-3 text-xs text-muted-foreground">
          Audio guidance isn't supported in this browser. The written script
          below will still guide you.
        </p>
      )}
      {supported && status !== "idle" && (
        <p className="mt-3 text-xs text-[var(--gold-deep)]">
          Now guiding: Step {currentStep + 1} of {steps.length} · {speed}x
          {status === "paused" ? " · paused" : ""}
        </p>
      )}
    </section>
  );
}
